import React from 'react';
import { Grid } from '@mui/material';
import { FieldComponents } from './FieldRegistry';
import { useBindingScopes } from './binding/scopes';
import { satisfies } from './binding/predicate';
import { PointedFieldsProvider, useIsPointed } from './binding/PointedFields';

const DEFAULT_COLUMNS = 12;
const DEFAULT_SIZE = 12;

/** How a field being pointed at is marked, drawn outside the flow so nothing moves for it. */
const POINTED = {
  outline: '2px solid',
  outlineColor: 'error.main',
  outlineOffset: '2px',
  borderRadius: '4px',
};

/**
 * One field in its cell, marked while something is pointing at it.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.field - The field descriptor.
 * @param {String} props.basePath - Where in the form store this schema's values live.
 * @param {Number|Object} props.columns - Columns in the enclosing grid.
 *
 * @returns {React.JSX.Element}
 */
const SchemaField = function SchemaField({ field, basePath, columns }) {
  const Component = FieldComponents[field.type];
  const pointed = useIsPointed(field.key);

  if (!Component) {
    console.warn(`Unsupported field type: ${field.type}`);

    return null;
  }

  return (
    <>
      {field.newRow && (
        <Grid size={columns} sx={{ height: 0, padding: '0!important' }} />
      )}
      <Grid
        size={field.size ?? DEFAULT_SIZE}
        offset={field.offset}
        sx={pointed ? POINTED : undefined}
      >
        <Component basePath={basePath} field={field} />
      </Grid>
    </>
  );
};

/**
 * Lays out a schema's fields.
 *
 * Every surface renders through here, so layout is declared in the descriptor rather than written
 * per page. The container is a 12-column grid unless `columns` says otherwise, and each field
 * chooses its own width, position and wrapping:
 *
 *   size    - columns to span. A number, a breakpoint object ({ xs: 12, md: 6 }), 'grow' or 'auto'.
 *             Defaults to the full width, so a descriptor that says nothing stacks its fields.
 *   offset  - columns to skip before the field. Same shapes as size.
 *   newRow  - start a new row regardless of the space left.
 *   visibleWhen - the predicate deciding whether it is drawn at all.
 *
 * @param {String} basePath - Where in the form store this schema's values live.
 * @param {Array} fields - The schema.
 * @param {Number|Object} columns - Columns in the grid.
 * @param {Number|Object} spacing - Gap between fields, in theme spacing units.
 */
export const SchemaRenderer = ({
  basePath,
  fields,
  columns = DEFAULT_COLUMNS,
  spacing = 1,
}) => {
  const scopes = useBindingScopes({
    basePath,
    reads: (fields ?? []).some(({ visibleWhen }) => visibleWhen),
  });

  return (
    <PointedFieldsProvider>
      <Grid
        container
        columns={columns}
        spacing={spacing}
        sx={{ width: '100%' }}
      >
        {fields
          ?.filter((field) => satisfies(field.visibleWhen, scopes))
          .map((field) => (
            <SchemaField
              key={field.key}
              field={field}
              basePath={basePath}
              columns={columns}
            />
          ))}
      </Grid>
    </PointedFieldsProvider>
  );
};
